import { CommonModule } from "@angular/common";
import { LOCALE_ID, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { LayoutModule } from "app/bundles/layouts/layout.module";
import { PhotosGalleryFormComponent } from "app/bundles/requests/edit-request/photos-gallery-form/photos-gallery-form.component";
import { PhotosSelectModule } from "app/bundles/requests/edit-request/photos-select-form/photos-select-form.module";
import { RequestAlertComponent } from "app/bundles/requests/request-alert/request-alert.component";
import { RequestDownloadComponent } from "app/bundles/requests/request-download/request-download.component";
import { RequestFilterComponent } from "app/bundles/requests/request-filter/request-filter.component";
import { RequestMenuComponent } from "app/bundles/requests/request-menu/request-menu.component";
import { CommentApiService } from "app/bundles/requests/shared/api-services/comment-api/comment-api.service";
import { RequestApiService } from "app/bundles/requests/shared/api-services/request-api/request-api.service";
import { EditRequestService } from "app/bundles/requests/shared/services/edit-request.service";
import { FilterService } from "app/bundles/requests/shared/services/filter.service";
// import { PhotosCaptureFormComponent } from 'app/bundles/requests/edit-workorder/photos-take-form/photos-take-form.component';
import { GeocoderService } from "app/bundles/requests/shared/services/geocoder.service";
import { IconService } from "app/bundles/requests/shared/services/icon.service";
import { MapService } from "app/bundles/requests/shared/services/map.service";
import { RequestService } from "app/bundles/requests/shared/services/request.service";
import { StatusService } from "app/bundles/requests/shared/services/status.service";
import { UtilService } from "app/bundles/requests/shared/services/util.service";
import { ErrorMessageService } from "app/core/error-message.service";
import { ApiService } from "app/shared/security/api-services/api.service";
import { StorageService } from "app/shared/services/storage.service";
import { SharedModule } from "app/shared/shared.module";
import { environment } from "environments/environment";
import { DropdownModule } from "primeng/dropdown";
import { GalleriaModule } from "primeng/galleria";
import { EditRequestInBatchComponent } from "./edit-request-in-batch/edit-request-in-batch.component";
import { EditFormComponent } from "./edit-request/edit-form/edit-form.component";
import { EditRequestComponent } from "./edit-request/edit-request.component";
import { PhotosCaptureFormComponent } from "./edit-request/photos-take-form/photos-take-form.component";
import { HeaderInBatchComponent } from "./header-in-batch/header-in-batch.component";
import { ListMessageComponent } from "./list-message/list-message.component";
import { RequestListParentComponent } from "./request-list-parent/request-list-parent.component";
import { RequestMapListComponent } from "./request-map-list/request-map-list.component";
import { RequestSummaryListComponent } from "./request-summary-list/request-summary-list.component";
import { RequestComponent } from "./request/request.component";
import { RequestsRoutingModule } from "./requests-routing.module";
import { MockCommentApiService } from "./shared/api-services/comment-api/comment-api.mock.service";
import { CommentApi } from "./shared/api-services/comment-api/comment-api.models";
import { MockCurrentUserApiService } from "./shared/api-services/current-user-api/current-user-api.mock.service";
import { CurrentUserApi } from "./shared/api-services/current-user-api/current-user-api.models";
import { CurrentUserApiService } from "./shared/api-services/current-user-api/current-user-api.service";
import { MockEventApiService } from "./shared/api-services/event-api/event-api.mock.service";
import { EventApi } from "./shared/api-services/event-api/event-api.models";
import { EventApiService } from "./shared/api-services/event-api/event-api.service";
import { MockRequestApiService } from "./shared/api-services/request-api/request-api.mock.service";
import { RequestApi } from "./shared/api-services/request-api/request-api.models";
import { MockStatusApiService } from "./shared/api-services/status-api/status-api.mock.service";
import { StatusApi } from "./shared/api-services/status-api/status-api.models";
import { StatusApiService } from "./shared/api-services/status-api/status-api.service";
import { DropdownButtonComponent } from "./shared/controls/dropdown-button/dropdown-button.component";
import { EditRequestTemplateComponent } from "./shared/edit-request-template/edit-request-template.component";
import { CanUpdateActivityPipe } from "./shared/pipes/can-update-activity";
import { isInternalWorkorderRequestPipe } from "./shared/pipes/is-internal-workorder-request.pipe";
import { IsWorkorderRequestPipe } from "./shared/pipes/is-workorder-request.pipe";
import { PhonePipe } from "./shared/pipes/phone.pipe";
import { CommentService } from "./shared/services/comment.service";
import { CurrentUserService } from "./shared/services/current-user.service";
import { RequestResolverService } from "./shared/services/request-resolver.service";
import { SummaryInBatchComponent } from "./summary-in-batch/summary-in-batch.component";
import { ViewRequestComponent } from "./view-request/view-request.component";

const requestApiClientFactory = (
  apiService: ApiService,
  errorMessageService: ErrorMessageService,
): RequestApi => {
  let requestApiClientService: RequestApi;
  if (environment.apiRequestParameters.mock) {
    requestApiClientService = new MockRequestApiService();
  } else {
    requestApiClientService = new RequestApiService(
      apiService,
      errorMessageService,
    );
  }
  return requestApiClientService;
};

const statusApiClientFactory = (
  apiService: ApiService,
  errorMessageService: ErrorMessageService,
): StatusApi => {
  let statusApiClientService: StatusApi;
  if (environment.apiRequestParameters.mock) {
    statusApiClientService = new MockStatusApiService();
  } else {
    statusApiClientService = new StatusApiService(
      apiService,
      errorMessageService,
    );
  }
  return statusApiClientService;
};

const commentApiClientFactory = (
  apiService: ApiService,
  errorMessageService: ErrorMessageService,
): CommentApi => {
  let commentApiClientService: CommentApi;
  if (environment.apiRequestParameters.mock) {
    commentApiClientService = new MockCommentApiService();
  } else {
    commentApiClientService = new CommentApiService(
      apiService,
      errorMessageService,
    );
  }
  return commentApiClientService;
};

const eventApiClientFactory = (
  apiService: ApiService,
  errorMessageService: ErrorMessageService,
): EventApi => {
  let eventApiClientService: EventApi;
  if (environment.apiRequestParameters.mock) {
    eventApiClientService = new MockEventApiService();
  } else {
    eventApiClientService = new EventApiService(
      apiService,
      errorMessageService,
    );
  }
  return eventApiClientService;
};

const currentUserApiClientFactory = (
  apiService: ApiService,
  errorMessageService: ErrorMessageService,
): CurrentUserApi => {
  let currentUserApiClientService: CurrentUserApi;
  if (environment.apiRequestParameters.mock) {
    currentUserApiClientService = new MockCurrentUserApiService();
  } else {
    currentUserApiClientService = new CurrentUserApiService(
      apiService,
      errorMessageService,
    );
  }
  return currentUserApiClientService;
};
@NgModule({
  imports: [
    RequestsRoutingModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule.forChild(),
    SharedModule,
    LayoutModule,
    PhotosSelectModule,
    DropdownModule,
    GalleriaModule,
  ],
  declarations: [
    RequestComponent,
    RequestListParentComponent,
    RequestSummaryListComponent,
    RequestMapListComponent,
    RequestDownloadComponent,
    ListMessageComponent,
    RequestMenuComponent,
    ViewRequestComponent,
    DropdownButtonComponent,
    RequestFilterComponent,
    RequestAlertComponent,
    EditRequestComponent,
    RequestFilterComponent,
    EditFormComponent,
    PhotosGalleryFormComponent,
    PhotosCaptureFormComponent,
    PhonePipe,
    IsWorkorderRequestPipe,
    CanUpdateActivityPipe,
    HeaderInBatchComponent,
    EditRequestInBatchComponent,
    EditRequestTemplateComponent,
    SummaryInBatchComponent,
    isInternalWorkorderRequestPipe,
  ],
  providers: [
    ApiService,
    {
      provide: RequestApi,
      useFactory: requestApiClientFactory,
      deps: [ApiService, ErrorMessageService],
    },
    {
      provide: StatusApi,
      useFactory: statusApiClientFactory,
      deps: [ApiService, ErrorMessageService],
    },
    {
      provide: CommentApi,
      useFactory: commentApiClientFactory,
      deps: [ApiService, ErrorMessageService],
    },
    {
      provide: EventApi,
      useFactory: eventApiClientFactory,
      deps: [ApiService, ErrorMessageService],
    },
    {
      provide: CurrentUserApi,
      useFactory: currentUserApiClientFactory,
      deps: [ApiService, ErrorMessageService],
    },
    RequestService,
    StorageService,
    IconService,
    EditRequestService,
    // PhotosService,
    MapService,
    UtilService,
    FilterService,
    StatusService,
    CommentService,
    GeocoderService,
    RequestResolverService,
    CurrentUserService,
    { provide: LOCALE_ID, useValue: "fr-CA" },
  ],
})
export class RequestsModule {}
